import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import config from '@bingads-webui-component-legacy/config/index';
import { serverContextStore } from '@bingads-webui-campaign/global-context-store';
import {
  AssetEventFeature,
} from '@bingads-webui-campaign-react/copilot-common';

export const ResponsiveAdConfigs = {
  MAX_HEADLINE_SUGGESTIONS: 15,
  MAX_DESCRIPTION_SUGGESTIONS: 10,
};

export const getEmptyRecommendationsContext = (finalUrl, recommendationSource) => (recommendationSource === 'copilot' ? {
  finalUrl,
  recommendationSource,
  themes: [],
  headlines: [],
  longHeadlines: [],
  descriptions: [],
  imageAssets: [],
} : {
  finalUrl,
  recommendationSource,
  headlines: [],
  longHeadlines: [],
  descriptions: [],
});

const getBaseUrl = ({
  cid, aid, campaignId, api,
}) => {
  if (campaignId) {
    return `${config.get('odata.url')}${config.get('odata.path')}/Customers(${cid})/Accounts(${aid})/Campaigns(${campaignId})/${api}`;
  }
  return `${config.get('odata.url')}${config.get('odata.path')}/Customers(${cid})/Accounts(${aid})/${api}`;
};

export const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    // For sign up local environment, the hostname is ccui.devdev.bingads.microsoft.com
    if (hostname.indexOf('devdev') > -1) {
      return true;
    }
    return hostname.indexOf('ads.microsoft.com') === -1 &&
      hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
  };

  return oDataConfig;
};

export const getOnlineTextRecommendations = ({
  cid, aid, campaignId, finalUrl,
}) => {
  const url = getBaseUrl({
    cid,
    aid,
    campaignId,
    api: 'Default.CreateResponsiveSearchAdRecommendation',
  });

  const requestBody = {
    count: 1,
    business: {
      Website: finalUrl,
    },
    headlineCount: ResponsiveAdConfigs.MAX_HEADLINE_SUGGESTIONS,
    descriptionCount: ResponsiveAdConfigs.MAX_DESCRIPTION_SUGGESTIONS,
    caller: 'cmmt_ra',
  };
  return odata.post(url, {
    oDataToken: config.get('odata.token'),
    data: JSON.stringify(requestBody),
  });
};

const getOnlineTextRecommendationsNative = ({ cid, aid, finalUrl }) => {
  const query = {
    FinalURL: finalUrl,
    Locale: _.get(serverContextStore, ['state', 'serverContext', 'CurrentLocale'], 'en-US'),
  };

  const select = [
    'FinalUrls',
    'BusinessName',
    'Model.ResponsiveAd/Headlines',
    'Model.ResponsiveAd/LongHeadlines',
    'Model.ResponsiveAd/Descriptions',
  ];

  const url = getBaseUrl({
    cid,
    aid,
    api: 'Ads/Default.GetResponsiveAdRecommendation',
  });

  return odata.post(
    `${url}?$select=${select.join(',')}`,
    _.extend({ data: query }, { oDataToken: config.get('odata.token') })
  );
};

export const getOnlineNativeAndSearchTextRecommendations = ({
  cid, aid, campaignId, finalUrl,
}) => {
  const nativeRecommendationsPromise = getOnlineTextRecommendationsNative({
    cid, aid, finalUrl,
  });

  const searchRecommendationsPromise = getOnlineTextRecommendations({
    cid, aid, campaignId, finalUrl,
  });

  return Promise.all([nativeRecommendationsPromise, searchRecommendationsPromise])
    .then(([nativeRecommendations, searchRecommendations]) => ({
      value: [{
        Headlines: _.flatten(_.union(nativeRecommendations.value.map(v => v.Headlines), searchRecommendations.value.map(v => v.Headlines))),
        LongHeadlines: _.flatten(_.union(nativeRecommendations.value.map(v => v.LongHeadlines), searchRecommendations.value.map(v => v.LongHeadlines))),
        Descriptions: _.flatten(_.union(nativeRecommendations.value.map(v => v.Descriptions), searchRecommendations.value.map(v => v.Descriptions))),
      }],
    }));
};

export const getOnlineBusinessNameRecommendations = ({
  cid, aid, campaignId, finalUrl,
}) => {
  const url = getBaseUrl({
    cid,
    aid,
    campaignId,
    api: 'Default.GetExtractedBusinessInfo',
  });

  const requestBody = {
    url: finalUrl,
  };
  return odata.post(url, {
    oDataToken: config.get('odata.token'),
    data: JSON.stringify(requestBody),
  });
};

export const getOfflineRecommendations = ({
  cid, aid,
}) => {
  const url = getBaseUrl({
    cid,
    aid,
    api: 'Default.PerformanceMaxAssetRecommendations',
  });
  return odata.post(url, {
    oDataToken: config.get('odata.token'),
  });
};

export const convertOnlineTextRecommendationToOfflineFormat = (textRecommendations) => {
  if (_.isArray(textRecommendations) && textRecommendations.length > 0) {
    return {
      headlines: _.chain(textRecommendations[0].Headlines)
        .map(headline => ({
          Text: headline.Asset.Text,
          SuggestionId: headline.RecommendationId,
        }))
        .value(),
      longHeadlines: _.chain(textRecommendations[0].Descriptions)
        .map(description => ({
          Text: description.Asset.Text,
          SuggestionId: description.RecommendationId,
        }))
        .value(),
      descriptions: _.chain(textRecommendations[0].Descriptions)
        .map(description => ({
          Text: description.Asset.Text,
          SuggestionId: description.RecommendationId,
        }))
        .value(),
    };
  }

  return getEmptyRecommendationsContext();
};

export const getSuggestionIdsFromOfflineRecommendations = (recommendation) => {
  let suggestionIds = [];
  if (recommendation.BusinessName && recommendation.BusinessName.SuggestionId) {
    suggestionIds.push(recommendation.BusinessName.SuggestionId);
  }
  suggestionIds = suggestionIds.concat(_.pluck(recommendation.Descriptions, 'SuggestionId'));
  suggestionIds = suggestionIds.concat(_.pluck(recommendation.Headlines, 'SuggestionId'));
  suggestionIds = suggestionIds.concat(_.pluck(recommendation.Images, 'SuggestionId'));
  suggestionIds = suggestionIds.concat(_.pluck(recommendation.LongHeadlines, 'SuggestionId'));
  return suggestionIds;
};

export class SuggestionsTracker {
  constructor() {
    this.trackingData = {
      allTextSuggestions: {
        descriptions: [],
        headlines: [],
        longHeadlines: [],
        businessName: [],
      },
    };
  }

  add(finalUrl, isFinalUrlFromOfflineRecommendations, suggestions, suggestionsReport) {
    this.trackingData = this.trackingData || {};
    this.trackingData.finalUrl = finalUrl;
    this.trackingData.isFinalUrlFromOfflineRecommendations = isFinalUrlFromOfflineRecommendations;

    if (suggestions) {
      this.trackingData.allTextSuggestions = {
        descriptions: _.uniq((this.trackingData.allTextSuggestions.descriptions).concat(suggestions.descriptions)),
        headlines: _.uniq((this.trackingData.allTextSuggestions.headlines).concat(suggestions.headlines)),
        longHeadlines: _.uniq((this.trackingData.allTextSuggestions.longHeadlines).concat(suggestions.longHeadlines)),
        businessName: suggestions.businessName ?
          _.uniq([...this.trackingData.allTextSuggestions.businessName, suggestions.businessName]) :
          this.trackingData.allTextSuggestions.businessName,
      };
    }
    if (_.isArray(suggestionsReport.view)) {
      this.trackingData.view = _.uniq((this.trackingData.view || []).concat(suggestionsReport.view));
    }
    if (_.isArray(suggestionsReport.apply)) {
      this.trackingData.apply = _.uniq((this.trackingData.apply || []).concat(suggestionsReport.apply));
    }
    if (_.isArray(suggestionsReport.returned)) {
      this.trackingData.returned = _.uniq((this.trackingData.returned || []).concat(suggestionsReport.returned));
    }
    if (_.isArray(suggestionsReport.selectedImageSuggestions)) {
      this.trackingData.selectedImageSuggestions = _.uniq((this.trackingData.selectedImageSuggestions || []).concat(suggestionsReport.selectedImageSuggestions), false, item => item.suggestionId);
    }
  }
}

const uniqByRecommendationId = arr => _.uniq(arr, false, item => item.RecommendationId);

export const uniqAssetRecommendationsInThemes = (themes) => {
  const headlines = [];
  const longHeadlines = [];
  const descriptions = [];
  const imageAssets = [];

  _.each(themes, (theme) => {
    if (theme.headlines) {
      headlines.push(...theme.headlines);
    }
    if (theme.longHeadlines) {
      longHeadlines.push(...theme.longHeadlines);
    }
    if (theme.descriptions) {
      descriptions.push(...theme.descriptions);
    }
    if (theme.images) {
      imageAssets.push(...theme.images);
    }
  });

  return {
    headlines: uniqByRecommendationId(headlines),
    longHeadlines: uniqByRecommendationId(longHeadlines),
    descriptions: uniqByRecommendationId(descriptions),
    imageAssets: _.uniq(imageAssets, false, item => item.suggestionId),
  };
};

export const getFeatureForAssetEvent = ({ isSignup, isDisplayPlus, isMMA }) => {
  if (isSignup) {
    return AssetEventFeature.PMAX_LITE;
  }
  if (isDisplayPlus) {
    return AssetEventFeature.DISPLAY_PLUS;
  }
  if (isMMA) {
    return AssetEventFeature.MMA;
  }
  return AssetEventFeature.PMAX;
};
